import { getUnbookedRealEstateLoans } from './selectors.js';

// What the questionnaire actually recorded, with the difference between "zero"
// and "nobody answered" kept intact.
//
// The rest of the assessment reads its numbers through `toNumber`, which turns
// a blank, a typo and a real zero into the same 0. That is the right trade for
// a report that totals things. It is the wrong one for this assessment, whose
// first promise to the prospect is printed under the figures: an unanswered
// field is not zero. So this module reads the raw strings the questionnaire
// stores and keeps four states apart:
//
//   known     a finite number at or above zero, an explicit 0 included
//   blank     nothing was entered
//   invalid   something was entered that is not a number
//   conflict  the advisor has said in Prepare that the entry is unresolved
//
// Only `known` is ever shown as a figure. Everything else becomes a question
// asked in the room, which is why a sparse household still gets a whole
// meeting instead of a wall of empty cards.
//
// There is no separate advisor copy of any figure. The questionnaire is the
// one record, the meeting edits it in place, and eMoney and this assessment
// read the same numbers (Eugene and John, 2026-09-29). The only things kept
// under `assessment.meta.prepare` are about the assessment itself: which
// statement analysis was accepted and what the takeaway includes.

export const PREPARE_PATH = 'assessment.meta.prepare';

// Matches the prototype's `known()`. Negative is not "known" because every
// figure this assessment shows is a balance, a spend or an income, and a
// negative one of those is a data-entry error rather than a fact to print.
export function isKnown(field) {
  return Boolean(field) && field.state === 'known';
}

export function amountOf(field) {
  return isKnown(field) ? field.value : null;
}

// A field is the unit this module traffics in: the number when there is one,
// and always the reason when there is not.
function field(state, value, raw, source) {
  return { state, value: state === 'known' ? value : null, raw: raw ?? '', source: source || null };
}

export function readAmount(raw, source) {
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return field('blank', null, raw, source);
  }
  const text=String(raw).trim();
  if(!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text)) return field('invalid', null, raw, source);
  const n = Number(text.replace(/,/g, ''));
  if (!Number.isFinite(n)) return field('invalid', null, raw, source);
  if (n < 0) return field('invalid', null, raw, source);
  return field('known', n, raw, source);
}

// A total over rows that may be partly unanswered. The sum of what is known is
// still worth showing, but the caller has to know it is a subtotal, so both
// facts come back together rather than one standing in for the other.
export function sumKnown(rows, key, source) {
  const list = Array.isArray(rows) ? rows : [];
  let total = 0;
  let knownRows = 0;
  let missingRows = 0;
  for (const row of list) {
    const f = readAmount(row?.[key], source);
    if (isKnown(f)) {
      total += f.value;
      knownRows += 1;
    } else if (String(row?.[key] ?? '').trim() !== '' || rowHasContent(row)) {
      missingRows += 1;
    }
  }
  const complete = knownRows > 0 && missingRows === 0;
  return {
    ...field(knownRows > 0 ? 'known' : 'blank', total, '', source),
    knownRows,
    missingRows,
    complete,
    partial: knownRows > 0 && missingRows > 0,
  };
}

// A row the advisor started but left without an amount still counts as missing.
// An entirely empty row is just an unused line in the form and is ignored.
function rowHasContent(row) {
  if (!row || typeof row !== 'object') return false;
  return Object.entries(row).some(
    ([k, v]) => !k.startsWith('_') && typeof v === 'string' && v.trim() !== ''
  );
}

const ASSET_TABLES = ['realEstate', 'taxDeferred', 'roth', 'taxable', 'cashCd', 'plan529', 'hsa', 'businessOther'];

// Which questionnaire table a balance sits in IS its tax treatment; there is no
// separate field. Roth means Roth: HSA and education accounts remain in total
// assets but are outside these three investment categories. An HSA must never
// be presented as a Roth balance.
const POOL_TABLES = {
  taxDeferred: ['taxDeferred'],
  roth: ['roth'],
  taxable: ['taxable', 'cashCd'],
};

const INCOME_TABLES = [
  ['employment', 'Employment'],
  ['socialSecurity', 'Social Security'],
  ['pension', 'Pension'],
  ['other', 'Other income'],
];

const STATUS_WORDS = {
  Employed: 'Working',
  'Self Employed': 'Working',
  Retired: 'Retired',
  'Not Employed': 'Not working',
};

export const STATUS_CHOICES = ['Working', 'Retired', 'Not working'];

function trimmed(v) {
  return typeof v === 'string' ? v.trim() : '';
}

export function householdName(data) {
  const f = data?.family || {};
  const first = trimmed(f.client1Nickname) || trimmed(f.client1FirstName);
  const second = trimmed(f.client2Nickname) || trimmed(f.client2FirstName);
  if (first && second) return `${first} and ${second}`;
  return first || second || '';
}

function yearFromDate(raw) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed(raw));
  if (m) return Number(m[1]);
  const d = new Date(trimmed(raw));
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
}

// The questionnaire takes a retirement date OR an age, never both, and the two
// need different arithmetic to reach the one thing this assessment shows: a
// year. An age with no current age to measure it against is unresolved rather
// than absent, because the advisor did answer and the answer cannot be used.
export function readRetirementYear(data, who, asOfYear) {
  const person = data?.employment?.[who] || {};
  const family = data?.family || {};

  const fromDate = yearFromDate(person.retirementDate);
  if (fromDate) return { ...field('known', fromDate, person.retirementDate, 'date'), person: who };

  const age = readAmount(person.retirementAge, 'age');
  if (isKnown(age)) {
    const now = readAmount(family[`${who}Age`], 'age');
    if (isKnown(now)) {
      const year = asOfYear + Math.round(age.value - now.value);
      return { ...field('known', year, person.retirementAge, 'age'), person: who };
    }
    return { ...field('conflict', null, person.retirementAge, 'age'), person: who };
  }
  return { ...field('blank', null, '', null), person: who };
}

// The household retires when the later of the two does, because the meeting is
// about the household's own timeline and the earlier date does not end the
// second income.
function householdRetirement(data, asOfYear, hasSpouse) {
  const one = readRetirementYear(data, 'client1', asOfYear);
  const two = hasSpouse ? readRetirementYear(data, 'client2', asOfYear) : null;
  const candidates = [one, two].filter(f => f && isKnown(f));
  if (!candidates.length) {
    if ((two && two.state === 'conflict') || one.state === 'conflict') {
      return field('conflict', null, '', 'employment');
    }
    return field('blank', null, '', 'employment');
  }
  const latest = candidates.reduce((a, b) => (b.value > a.value ? b : a));
  return { ...latest, source: 'employment' };
}

function readStatus(data, hasSpouse) {
  const raw = trimmed(data?.employment?.client1?.status);
  const word = STATUS_WORDS[raw];
  if (word) return field('known', null, word, 'employment');
  if (hasSpouse) {
    const spouse = STATUS_WORDS[trimmed(data?.employment?.client2?.status)];
    if (spouse) return field('known', null, spouse, 'employment');
  }
  return field('blank', null, '', 'employment');
}

// Spending is the questionnaire's living expenses plus any expense row with no
// start or end date, which is the same split `getAssessmentDefaults` makes: a
// dated row belongs to a particular year and this figure is a current-year one.
// If living expenses is unanswered the whole figure is unanswered, even when
// stray rows exist, because a household does not spend only what is itemised.
function readSpending(data) {
  const te = data?.taxesExpenses || {};
  const base = readAmount(te.livingExpenses, 'spending');
  if (!isKnown(base)) return base;
  const rows = Array.isArray(te.expenses) ? te.expenses : [];
  let total = base.value;
  let missingRows = 0;
  for (const row of rows) {
    if (trimmed(row?.startDate) || trimmed(row?.endDate)) continue;
    const f = readAmount(row?.amount, 'spending');
    if (isKnown(f)) total += f.value;
    else if (rowHasContent(row)) missingRows += 1;
  }
  return { ...field('known', total, base.raw, 'spending'), missingRows };
}

// Each row remembers where it lives (table and position) so the meeting can
// write an amount back to the very row the questionnaire holds.
function readIncomeRows(data) {
  const income = data?.income || {};
  const rows = [];
  for (const [key, label] of INCOME_TABLES) {
    const list = Array.isArray(income[key]) ? income[key] : [];
    list.forEach((row, index) => {
      if (!rowHasContent(row)) return;
      rows.push({
        key,
        table: key,
        index,
        uid: row._uid || null,
        label: trimmed(row.description) || label,
        kind: label,
        owner: trimmed(row.owner),
        amount: readAmount(row.annualAmount, 'income'),
      });
    });
  }
  return rows;
}

// What the household said it wants, in its own words: the questionnaire's
// Goals box, one line per thought. Shown back verbatim, because "we heard you"
// is the point and a paraphrase would not be.
function readPools(data) {
  const assets = data?.assets || {};
  const out = {};
  for (const [pool, tables] of Object.entries(POOL_TABLES)) {
    let total = 0;
    let knownRows = 0;
    let missingRows = 0;
    for (const table of tables) {
      const s = sumKnown(assets[table], 'marketValue', 'assets');
      total += s.value || 0;
      knownRows += s.knownRows;
      missingRows += s.missingRows;
    }
    out[pool] = {
      ...field(knownRows > 0 ? 'known' : 'blank', total, '', 'assets'),
      knownRows,
      missingRows,
      partial: knownRows > 0 && missingRows > 0,
    };
  }
  return out;
}

function readAssets(data) {
  const assets = data?.assets || {};
  let total = 0;
  let knownRows = 0;
  let missingRows = 0;
  for (const table of ASSET_TABLES) {
    const s = sumKnown(assets[table], 'marketValue', 'assets');
    total += s.value || 0;
    knownRows += s.knownRows;
    missingRows += s.missingRows;
  }
  return {
    ...field(knownRows > 0 ? 'known' : 'blank', total, '', 'assets'),
    knownRows,
    missingRows,
    partial: knownRows > 0 && missingRows > 0,
  };
}

// Liabilities follow the convention the rest of the app already uses: the
// liability rows, plus any real-estate loan that was never booked as one. Doing
// it differently here would put two different net-worth figures in front of the
// same advisor on the same afternoon.
function readLiabilities(data) {
  const items = data?.liabilities?.items;
  const booked = sumKnown(items, 'amount', 'liabilities');
  // The shared helper is written for a caller that runs everything through
  // `toNumber`, so it can hand back something that is not a number. Coercing
  // here keeps a string from turning the whole total into one by concatenation.
  let unbooked = 0;
  try {
    const raw = getUnbookedRealEstateLoans(data?.assets || {}, items);
    unbooked = Number(raw);
    if (!Number.isFinite(unbooked) || unbooked < 0) unbooked = 0;
  } catch {
    unbooked = 0;
  }
  const knownRows = booked.knownRows + (unbooked > 0 ? 1 : 0);
  return {
    ...field(knownRows > 0 ? 'known' : 'blank', (booked.value || 0) + unbooked, '', 'liabilities'),
    knownRows,
    missingRows: booked.missingRows,
    partial: knownRows > 0 && booked.missingRows > 0,
  };
}

function readTold(data) {
  const text = trimmed(data?.goals?.clientGoals);
  if (!text) return [];
  return text
    .split(/\r?\n|;/)
    .map(s => s.replace(/^[\s\-•*]+/, '').trim())
    .filter(Boolean);
}

export function readPreparation(data) {
  const prepare = data?.assessment?.meta?.prepare;
  return prepare && typeof prepare === 'object' ? prepare : {};
}

// The people in the household, with the employment facts the starting point
// shows and edits per person rather than as one blended answer.
function readPeople(data, hasSpouse, asOfYear) {
  const family = data?.family || {};
  const who = hasSpouse ? ['client1', 'client2'] : ['client1'];
  return who.map(person => {
    const emp = data?.employment?.[person] || {};
    const raw = trimmed(emp.status);
    return {
      who: person,
      name: trimmed(family[`${person}Nickname`]) || trimmed(family[`${person}FirstName`]) || (person === 'client1' ? 'Client' : 'Spouse'),
      age: readAmount(family[`${person}Age`], 'age'),
      status: STATUS_WORDS[raw] ? field('known', null, STATUS_WORDS[raw], 'employment') : field('blank', null, '', 'employment'),
      statusRaw: raw,
      retirement: readRetirementYear(data, person, asOfYear),
      retirementDate: trimmed(emp.retirementDate),
      retirementAge: trimmed(emp.retirementAge),
    };
  });
}

/**
 * The whole recorded picture, as one object every other surface reads.
 *
 * `options.asOf` exists so a test and a print run agree about what year it is;
 * nothing else in here reads the clock.
 */
export function getRecordedFacts(data, options = {}) {
  const asOf = options.asOf instanceof Date ? options.asOf : new Date();
  const asOfYear = asOf.getFullYear();
  const hasSpouse = Boolean(data?._flags?.hasSpouse) || Boolean(trimmed(data?.family?.client2FirstName));

  const status = readStatus(data, hasSpouse);

  let retirement = householdRetirement(data, asOfYear, hasSpouse);
  if (isKnown(retirement) && (!Number.isInteger(retirement.value) || retirement.value < 1900 || retirement.value > 2200)) {
    retirement = field('invalid', null, retirement.raw, retirement.source);
  }

  const spending = readSpending(data);

  const incomeRows = readIncomeRows(data);
  const incomeKnown = incomeRows.filter(r => isKnown(r.amount));
  const incomeTotal = incomeKnown.reduce((s, r) => s + r.amount.value, 0);
  const everyRowKnown = incomeRows.length > 0 && incomeKnown.length === incomeRows.length;
  // Complete means every source that was written down carries an amount. A row
  // started and left blank is a question, not a zero, and holds the comparison
  // back until it is answered in the room.
  const income = {
    rows: incomeRows,
    ...field(incomeKnown.length > 0 ? 'known' : 'blank', incomeTotal, '', 'income'),
    knownRows: incomeKnown.length,
    missingRows: incomeRows.length - incomeKnown.length,
    everyRowKnown,
    complete: everyRowKnown,
  };

  const assets = readAssets(data);
  const liabilities = readLiabilities(data);
  const assetsPartial = Boolean(assets.partial);

  const pools = readPools(data);
  // Every balance sits in a questionnaire table, and the table is its tax
  // treatment, so nothing is left over to classify. Kept as a blank field
  // because the goal sheet and the takeaway still know how to show one.
  const unclassified = field('blank', null, '', 'assets');

  // Net worth needs both sides known and every row carrying an amount. A net
  // figure computed from half a balance sheet is the most confidently wrong
  // number this page could print.
  const net =
    isKnown(assets) && isKnown(liabilities) && !assetsPartial && !liabilities.partial
      ? field('known', assets.value - liabilities.value, '', 'derived')
      : field('blank', null, '', 'derived');

  return {
    asOfYear,
    name: householdName(data),
    hasSpouse,
    people: readPeople(data, hasSpouse, asOfYear),
    status,
    retirement,
    spending,
    income,
    assets,
    liabilities,
    assetsPartial,
    net,
    pools,
    unclassified,
    told: readTold(data),
    planningFocus: '',
    intakeNotes: trimmed(data?.goals?.clientGoals),
    advisorNotes: trimmed(data?.goals?.advisorNotes),
    period: `${asOfYear} · current year`,
  };
}

/**
 * The one comparison this assessment is willing to draw, and the conditions it
 * refuses to draw it under.
 *
 * It needs every income row to carry an amount, the advisor to have said the
 * list is complete, a spending figure, and no conflict on either side. Anything
 * less gives a subtotal and a question instead of a difference.
 */
export function getComparison(facts) {
  const ready =
    facts.income.complete &&
    isKnown(facts.spending) &&
    facts.spending.state !== 'conflict' && !facts.spending.missingRows;
  if (!ready) {
    return { ready: false, gap: null, direction: null, income: amountOf(facts.income), spending: amountOf(facts.spending) };
  }
  const gap = facts.spending.value - facts.income.value;
  return {
    ready: true,
    gap,
    direction: gap > 0 ? 'short' : gap < 0 ? 'above' : 'level',
    income: facts.income.value,
    spending: facts.spending.value,
  };
}

/**
 * What still has to be settled before the figures above mean anything, phrased
 * as the next question rather than as a defect.
 */
export function getClarifications(facts) {
  const out = [];
  if (facts.spending.state === 'conflict') {
    out.push({ key: 'spend-conflict', text: 'Resolve the spending amount and whether it is a monthly or an annual figure.' });
  } else if (!isKnown(facts.spending)) {
    out.push({ key: 'spend', text: 'Record annual household spending.' });
  }
  if (!facts.income.everyRowKnown) {
    out.push({
      key: 'income',
      text: facts.income.rows.length
        ? 'Complete the amounts on every income source.'
        : 'Record income amounts, or record explicitly that there is no current income.',
    });
  }
  if (facts.retirement.state === 'conflict') {
    out.push({ key: 'retire', text: 'Confirm the conflicting retirement timing entries.' });
  }
  if (facts.assetsPartial || !isKnown(facts.liabilities) || facts.liabilities.partial) {
    out.push({ key: 'balance-sheet', text: 'Complete the asset and liability entries.' });
  }
  if (isKnown(facts.unclassified) && facts.unclassified.value > 0) {
    out.push({ key: 'unclassified', text: 'Confirm the tax type of the remaining account balances.' });
  } else if (!Object.values(facts.pools).some(isKnown)) {
    out.push({ key: 'pools', text: 'Record the investment balances and their account types.' });
  }
  return out;
}

/**
 * Readiness, per observation rather than as one completion score. A missing
 * income amount must not switch off the account facts, and a full set of
 * accounts must not imply the income is settled.
 */
export function getReadiness(facts) {
  const comparison = getComparison(facts);
  const anyPool = Object.values(facts.pools).some(isKnown);
  return [
    {
      key: 'comparison',
      label: 'Income and spending',
      ready: comparison.ready,
      note: comparison.ready ? 'Comparison available' : 'Comparison withheld',
    },
    {
      key: 'pools',
      label: 'Account categories',
      ready: anyPool,
      note: anyPool ? 'Balances available' : 'No balances recorded',
    },
    {
      key: 'balance-sheet',
      label: 'Assets and liabilities',
      ready: isKnown(facts.net),
      note: isKnown(facts.net) ? 'Net of recorded items available' : 'Net withheld',
    },
  ];
}
